import React, { useEffect, useState } from "react";
import { Plus, Tag, Check, X, Search, Zap, Settings2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import TagManager from "./TagManager";

export const TAG_COLORS = {
  red: "bg-red-100 text-red-800 border-red-300",
  orange: "bg-orange-100 text-orange-800 border-orange-300",
  amber: "bg-amber-100 text-amber-800 border-amber-300",
  emerald: "bg-emerald-100 text-emerald-800 border-emerald-300",
  teal: "bg-teal-100 text-teal-800 border-teal-300",
  sky: "bg-sky-100 text-sky-800 border-sky-300",
  indigo: "bg-indigo-100 text-indigo-800 border-indigo-300",
  violet: "bg-violet-100 text-violet-800 border-violet-300",
  rose: "bg-rose-100 text-rose-800 border-rose-300",
  slate: "bg-slate-100 text-slate-700 border-slate-300",
};

let cache = null;
const listeners = new Set();
export const refreshTagFamilies = () =>
  api.get("/configuration/tags").then((r) => {
    cache = r.data.families || [];
    listeners.forEach((l) => l(cache));
    return cache;
  });

export function useTagFamilies() {
  const [fams, setFams] = useState(cache || []);
  useEffect(() => {
    listeners.add(setFams);
    if (!cache) refreshTagFamilies().catch(() => {});
    return () => listeners.delete(setFams);
  }, []);
  return fams;
}

export const tagIndex = (fams) =>
  Object.fromEntries(fams.flatMap((f) => f.tags.map((t) => [t.id, { ...t, color: f.color, family: f.name }])));

export function TagPill({ tag, onRemove, size = "sm" }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border font-semibold transition-transform hover:-translate-y-px ${TAG_COLORS[tag.color] || TAG_COLORS.slate} ${size === "xs" ? "text-[9px] px-1.5 py-0" : "text-[11px] px-2 py-0.5"}`}
      title={tag.family ? `${tag.family} · ${tag.name}` : tag.name}
      data-testid={`tag-pill-${tag.id}`}
    >
      {tag.name}
      {onRemove && (
        <button type="button" onClick={(e) => { e.stopPropagation(); onRemove(); }} className="opacity-60 hover:opacity-100" data-testid={`tag-remove-${tag.id}`}>
          <X className="w-2.5 h-2.5" />
        </button>
      )}
    </span>
  );
}

export function TagList({ ids, size = "xs" }) {
  const idx = tagIndex(useTagFamilies());
  const tags = (ids || []).map((i) => idx[i]).filter(Boolean);
  if (!tags.length) return null;
  return <div className="flex flex-wrap gap-1 mt-1">{tags.map((t) => <TagPill key={t.id} tag={t} size={size} />)}</div>;
}

export function DossierTagsEditor({ dossierId, ids, onChange, endpoint }) {
  const fams = useTagFamilies();
  const idx = tagIndex(fams);
  const [sel, setSel] = useState(ids || []);
  useEffect(() => setSel(ids || []), [ids]);

  const save = async (next) => {
    const prev = sel;
    setSel(next);
    try {
      await api.put(endpoint || `/dossiers/${dossierId}/tags`, { tag_ids: next });
      onChange?.();
    } catch {
      setSel(prev);
      toast.error("Mise à jour des tags impossible");
    }
  };
  const toggle = (id) => save(sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]);
  const [q, setQ] = useState("");
  const [famId, setFamId] = useState("");
  const [manage, setManage] = useState(false);
  const needle = q.trim().toLowerCase();
  const shown = fams.map((f) => ({ ...f, tags: f.tags.filter((t) => !needle || t.name.toLowerCase().includes(needle)) })).filter((f) => f.tags.length || !needle);
  const exact = fams.some((f) => f.tags.some((t) => t.name.toLowerCase() === needle));
  const create = async () => {
    const fid = famId || fams[0]?.id;
    if (!needle || !fid) return;
    try {
      const { data } = await api.post(`/configuration/tags/${fid}/tags`, { name: q.trim() });
      await refreshTagFamilies();
      setQ("");
      if (!sel.includes(data.tag.id)) save([...sel, data.tag.id]);
      toast.success(`Tag « ${data.tag.name} » créé`);
    } catch {
      toast.error("Création du tag impossible");
    }
  };

  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="dossier-tags">
      <Tag className="w-3 h-3 text-norea-textMuted" />
      {sel.map((i) => idx[i]).filter(Boolean).map((t) => <TagPill key={t.id} tag={t} onRemove={() => toggle(t.id)} />)}
      <Popover onOpenChange={(o) => !o && setManage(false)}>
        <PopoverTrigger asChild>
          <button type="button" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-dashed border-norea-border text-[11px] text-norea-textMuted hover:bg-norea-surfaceHover hover:text-norea-text transition-colors" data-testid="btn-add-tag">
            <Plus className="w-3 h-3" /> Tag
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className={`${manage ? "w-[26rem]" : "w-80"} p-3 space-y-3 max-h-[70vh] overflow-y-auto`} data-testid="tag-picker">
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-wider font-semibold text-norea-textMuted">{manage ? "Gérer les tags" : "Tags du dossier"}</span>
            <button type="button" onClick={() => { if (!manage) refreshTagFamilies(); setManage(!manage); }} className="inline-flex items-center gap-1 text-[11px] text-norea-primary hover:underline" data-testid="tag-manage-toggle">
              {manage ? <><ArrowLeft className="w-3 h-3" /> Retour</> : <><Settings2 className="w-3 h-3" /> Gérer</>}
            </button>
          </div>
          {manage ? <TagManager onChange={onChange} /> : <>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-norea-textDisabled" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && !exact && create()} placeholder="Rechercher ou créer un tag…" className="w-full pl-7 pr-2 py-1.5 text-xs border border-norea-border rounded focus:ring-1 focus:ring-norea-primary outline-none" data-testid="tag-search-input" />
          </div>
          {needle && !exact && fams.length > 0 && (
            <div className="flex items-center gap-1.5 p-2 rounded border border-dashed border-norea-border bg-norea-bg" data-testid="tag-create-row">
              <button type="button" onClick={create} className="inline-flex items-center gap-1 text-xs font-medium text-norea-primary hover:underline whitespace-nowrap" data-testid="tag-create-btn">
                <Plus className="w-3 h-3" /> Créer « {q.trim()} »
              </button>
              <span className="text-[11px] text-norea-textMuted">dans</span>
              <select value={famId || fams[0]?.id} onChange={(e) => setFamId(e.target.value)} className="flex-1 min-w-0 text-[11px] border border-norea-border rounded px-1 py-0.5 bg-white" data-testid="tag-create-family">
                {fams.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </div>
          )}
          {fams.length === 0 && <div className="text-xs text-norea-textMuted italic">Aucune famille de tags. Créez-en dans Configuration &gt; Listes.</div>}
          {shown.map((f) => (
            <div key={f.id}>
              <div className="text-[10px] uppercase tracking-wider text-norea-textMuted font-semibold mb-1.5 flex items-center gap-1">{f.name}{f.auto_field && <Zap className="w-2.5 h-2.5 text-amber-500" title="Posé automatiquement d'après le dossier" />}</div>
              <div className="flex flex-wrap gap-1.5">
                {f.tags.map((t) => {
                  const on = sel.includes(t.id);
                  return (
                    <button key={t.id} type="button" onClick={() => toggle(t.id)}
                      className={`inline-flex items-center gap-1 rounded-full border text-[11px] px-2 py-0.5 font-semibold transition-opacity ${TAG_COLORS[f.color] || TAG_COLORS.slate} ${on ? "" : "opacity-45 hover:opacity-80"}`}
                      data-testid={`tag-option-${t.id}`}>
                      {on && <Check className="w-3 h-3" />}{t.name}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          </>}
        </PopoverContent>
      </Popover>
    </div>
  );
}
